<!--
  제조사별 모바일 하단 탭 설정.

  MenuManageModal.vue(계정별 권한 관리, 743줄)와 목적이 다르다.
    MenuManageModal = 이 계정이 무엇을 볼 자격이 있는가 (보안, 계정별)
    이 화면          = 그중 무엇을 폰 하단에 꺼낼까      (배치, 제조사별)

  그래서 읽기·쓰기·수정·삭제·엑셀 권한과 readOptions(전체조회/지점전체조회/
  본인계정만조회)를 다루지 않는다. 그것들은 계정별 권한의 영역이고,
  덴티움 ERP 영업사원 처리가 거기에 걸려 있어 여기서 건드리면 안 된다.

  실제 노출은 폰에서 교집합으로 정해진다.
    표시할 탭 = 제조사가 고른 탭 ∩ 그 계정의 privileges
  제조사가 잘못 골라도 권한 없는 계정에는 그 탭이 나오지 않는다.
-->
<template>
  <a-modal
    v-bind:open="showModal"
    width="720px"
    :closable="true"
    :footer="null"
    :mask-closable="false"
    :mask-style="{ backdropFilter: 'blur(2px)' }"
    :keyboard="false"
    :title="`하단 탭 설정${companyName ? ' — ' + companyName : ''}`"
    :centered="true"
    @cancel="uiHandler.closeModal"
  >
    <div class="mobile-tab-body">
      <!-- 선택된 탭 -->
      <div class="section-label">
        선택된 탭
        <span class="hint">
          드래그로 순서 변경 · 최대 {{ maxTabs }}개 ({{ selectedTabs.length }}/{{ maxTabs }})
        </span>
      </div>

      <!--
        자리를 예약당한 제조사(덴티움)와 아닌 곳은 안내가 달라야 한다.
        예전에는 두 문장을 항상 같이 띄웠는데, 브라이트몰이 붙지 않는
        제조사에도 붙는다고 말하는 셈이라 오해를 샀다.
        덴티움 id 를 화면이 알 필요는 없다 — 상한이 줄어 있으면 예약된 것이다.
      -->
      <p class="auto-note">
        마이페이지는 항상 마지막 탭으로 자동 추가됩니다.
        <template v-if="maxTabs < DEFAULT_MAX_TABS">
          이 제조사는 브라이트몰 주문 관리도 자동으로 붙어, 그 자리를 빼고 {{ maxTabs }}개까지 고를 수 있습니다.
        </template>
      </p>

      <div v-if="selectedTabs.length === 0" class="empty-box">
        아직 선택한 메뉴가 없습니다. 아래에서 골라 주세요.
      </div>

      <draggable
        v-else
        class="tab-list"
        ghost-class="drag-ghost"
        :list="selectedTabs"
        item-key="menuId"
        :options="{ handle: '.drag-btn' }"
      >
        <div v-for="tab in selectedTabs" :key="tab.menuId" class="tab-row">
          <section class="drag-btn"><i class="fa-solid fa-grip-vertical" /></section>
          <img v-if="tab.iconDefaultUrl" :src="tab.iconDefaultUrl" class="tab-icon" alt="" />
          <span v-else class="tab-icon tab-icon-none" />
          <span class="tab-name">{{ tab.menuName }}</span>
          <span class="tab-link">{{ tab.link }}</span>
          <button type="button" class="tab-remove" @click="uiHandler.removeTab(tab.menuId)">
            ✕
          </button>
        </div>
      </draggable>

      <!-- 추가할 메뉴 -->
      <div class="section-label mt">
        추가할 메뉴
        <span class="hint">제조사 메뉴 {{ selectableMenus.length }}개</span>
      </div>

      <div class="menu-picker">
        <label
          v-for="menu in selectableMenus"
          :key="menu._id"
          class="pick-item"
          :class="{ disabled: uiHandler.isBlocked(menu.link) || uiHandler.isPickDisabled(menu) }"
        >
          <input
            type="checkbox"
            :checked="uiHandler.isSelected(menu._id)"
            :disabled="uiHandler.isBlocked(menu.link) || uiHandler.isPickDisabled(menu)"
            @change="uiHandler.toggleMenu(menu)"
          />
          <img v-if="menu.iconDefaultUrl" :src="menu.iconDefaultUrl" class="pick-icon" alt="" />
          <span v-else class="pick-icon pick-icon-none" />
          <!--
            이름과 링크를 한 줄에 나란히 두면 좁은 칸에서 이름부터 잘린다
            ("목표실…", "매출…"). 어느 메뉴인지 알아볼 수 없으므로 위아래로 쌓는다.
          -->
          <span class="pick-text">
            <span class="pick-name">{{ menu.name }}</span>
            <span class="pick-link">{{ menu.link }}</span>
          </span>
        </label>
      </div>
      <p class="picker-note">
        흐린 항목은 폰에서 화면이 깨지는 갈래라 선택할 수 없습니다.
      </p>
    </div>

    <div class="mobile-tab-footer">
      <!--
        켜고 끄는 스위치는 두지 않는다 — 설정한다는 것이 곧 쓰겠다는 뜻이다.
        끄려면 선택을 비우고 저장하면 된다.
      -->
      <span class="footer-hint">
        선택을 모두 비우고 저장하면 이 제조사의 탭이 폰에서 사라집니다.
      </span>
      <div class="footer-btns">
        <a-button @click="uiHandler.closeModal">
          취소
        </a-button>
        <a-button type="primary" class="yellow-button" @click="uiHandler.save">
          저장
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { VueDraggableNext as draggable } from 'vue-draggable-next';
import { oneBtnDialog } from '@/utils/utils';

/*
 * 상한의 기본값. 실제 값은 조회 응답의 maxTabs 를 쓴다.
 *
 * 폰의 탭 자리는 모두 5개인데(375px 에서 탭 하나에 최소 75px)
 * 한 자리는 마이페이지가 고정으로 쓰므로 제조사 몫은 4개다.
 * 덴티움은 브라이트몰 주문 관리가 자동으로 붙어 자리를 하나 더 쓰므로
 * 서버가 3을 내려준다. 여기 값을 고쳐도 서버가 막으므로,
 * 이 상수는 응답을 받기 전이나 조회에 실패했을 때의 대비값일 뿐이다.
 */
const DEFAULT_MAX_TABS = 4;

/**
 * 폰에서 열면 화면이 깨지는 갈래.
 * admin-manufacturer 의 src/utils/mobileAccess.js 와 같은 목록이다.
 * 한쪽만 고치면 운영에서는 고를 수 있는데 폰에서는 안내 화면으로 튕긴다.
 */
const MOBILE_BLOCKED_PREFIXES = [
  '/events',
  '/events-v2',
  '/promotions',
  '/brand-settings',
  '/alimtalk',
  '/email-templates',
];

const readOnly = {
  event: { CloseModal: 'CloseModal' },
};

export default {
  name: 'MobileTabModal',
  components: { draggable },
  props: {
    showModal: { type: Boolean, required: true, default: false },
    companyId: { type: String, default: '' },
    companyName: { type: String, default: '' },
  },
  emits: Object.keys(readOnly.event),
  data() {
    return {
      uiHandler: this.ui(),
      apiHandler: this.api(),
      // 안내 문구에서 '상한이 줄었는가' 를 판단하는 데 쓴다.
      DEFAULT_MAX_TABS,
      // 서버가 이 제조사의 상한을 내려준다(덴티움 3, 그 외 4).
      maxTabs: DEFAULT_MAX_TABS,
      selectedTabs: [], // [{ menuId, menuName, link, iconDefaultUrl }]
      wholeMenus: [],
    };
  },
  computed: {
    // 제조사 메뉴만. 스튜디오는 메뉴가 아니라 권한 식별용 sentinel 이라 뺀다.
    selectableMenus() {
      return this.wholeMenus.filter(
        (m) => m.role === 'MANUFACTURER' && m.code !== 'STUDIO_ACCESS' && m.link !== '/studio',
      );
    },
  },
  watch: {
    // 모달이 열릴 때마다 그 제조사 설정을 새로 읽는다.
    showModal(open) {
      if (open) this.uiHandler.load();
    },
  },
  methods: {
    ui() {
      return {
        load: async () => {
          this.selectedTabs = [];
          this.maxTabs = DEFAULT_MAX_TABS;

          const menuRes = await this.apiHandler.getMenuList();
          this.wholeMenus = menuRes?.data?.docs || menuRes?.data || [];

          if (!this.companyId) return;
          const tabRes = await this.apiHandler.getMobileTabs(this.companyId);
          const body = tabRes?.data;
          if (!body) return;

          // 이 제조사의 상한. 덴티움은 브라이트몰 자리를 빼고 3이 온다.
          if (Number.isInteger(body.maxTabs) && body.maxTabs > 0) {
            this.maxTabs = body.maxTabs;
          }

          // 서버가 menus 를 조인해 menuName·link·아이콘까지 붙여 주므로 그대로 쓴다.
          this.selectedTabs = (body.tabs || []).map((t) => ({
            menuId: String(t.menuId),
            menuName: t.menuName,
            link: t.link,
            iconDefaultUrl: t.iconDefaultUrl,
          }));
        },

        isBlocked: (link) => {
          if (!link) return false;
          const clean = String(link).split('?')[0].split('#')[0];
          return MOBILE_BLOCKED_PREFIXES.some(
            (p) => clean === p || clean.startsWith(`${p}/`),
          );
        },

        isSelected: (menuId) =>
          this.selectedTabs.some((t) => t.menuId === String(menuId)),

        // 상한에 도달하면 아직 안 고른 항목만 잠근다. 이미 고른 것은 해제할 수 있어야 한다.
        isPickDisabled: (menu) =>
          this.selectedTabs.length >= this.maxTabs &&
          !this.selectedTabs.some((t) => t.menuId === String(menu._id)),

        toggleMenu: (menu) => {
          const id = String(menu._id);
          const at = this.selectedTabs.findIndex((t) => t.menuId === id);
          if (at >= 0) {
            this.selectedTabs.splice(at, 1);
            return;
          }
          if (this.selectedTabs.length >= this.maxTabs) return;
          this.selectedTabs.push({
            menuId: id,
            menuName: menu.name,
            link: menu.link,
            iconDefaultUrl: menu.iconDefaultUrl,
          });
        },

        removeTab: (menuId) => {
          const at = this.selectedTabs.findIndex((t) => t.menuId === menuId);
          if (at >= 0) this.selectedTabs.splice(at, 1);
        },

        save: async () => {
          if (!this.companyId) {
            await oneBtnDialog('저장 실패', '제조사 정보가 없습니다.', false);
            return;
          }

          const result = await this.apiHandler.saveMobileTabs({
            companyId: this.companyId,
            // order 는 서버가 배열 순서로 다시 매기지만, 의도를 분명히 남긴다.
            tabs: this.selectedTabs.map((t, i) => ({ menuId: t.menuId, order: i })),
          });
          if (!result) return;

          await oneBtnDialog('저장 완료', '하단 탭 설정을 저장했습니다.', true);
          this.uiHandler.closeModal();
        },

        closeModal: () => {
          this.$emit(readOnly.event.CloseModal, { ModalStatus: false });
        },
      };
    },
    api() {
      return {
        getMenuList: async () => {
          try {
            const response = await this.$apiInstance.post('/menus/list', {
              page: 1,
              limit: 100,
            });
            if (response.status !== 200 && response.status !== 201) return;
            return response;
          } catch (error) {
            console.log(error);
            await oneBtnDialog('메뉴 로드 실패', '메뉴 로드에 실패하였습니다.', false);
          }
        },
        getMobileTabs: async (companyId) => {
          try {
            const response = await this.$apiInstance.get(
              `/company-mobile-tabs/${companyId}`,
            );
            if (response.status !== 200 && response.status !== 201) return;
            return response;
          } catch (error) {
            console.log(error);
            await oneBtnDialog('설정 로드 실패', '하단 탭 설정을 불러오지 못했습니다.', false);
          }
        },
        saveMobileTabs: async (obj) => {
          try {
            const response = await this.$apiInstance.post('/company-mobile-tabs', obj);
            if (response.status !== 200 && response.status !== 201) return;
            return response;
          } catch (error) {
            console.log(error);
            const msg = error?.response?.data?.message || '하단 탭 설정 저장에 실패하였습니다.';
            await oneBtnDialog('저장 실패', msg, false);
          }
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.mobile-tab-body {
  padding: 4px 2px 0;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #8d939a;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  &.mt {
    margin-top: 20px;
  }

  .hint {
    font-weight: 400;
    font-size: 12px;
  }
}

.empty-box {
  border: 1px dashed #d1d6db;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: #acb2b9;
  font-size: 13px;
}

.tab-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tab-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid #e8ebed;
  border-radius: 8px;
  background: #fff;
}

.drag-btn {
  color: #acb2b9;
  cursor: grab;
}

.drag-ghost {
  opacity: 0.4;
}

.tab-icon,
.pick-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex: none;
}

.tab-icon-none,
.pick-icon-none {
  background: #f1f2f3;
  border-radius: 4px;
}

.tab-name {
  font-weight: 600;
  font-size: 13.5px;
}

.tab-link {
  color: #acb2b9;
  font-size: 12px;
}

.tab-remove {
  margin-left: auto;
  width: 22px;
  height: 22px;
  border: 1px solid #d1d6db;
  background: #fff;
  border-radius: 5px;
  color: #6e747c;
  line-height: 1;
}

.menu-picker {
  display: grid;
  /* 이름을 자르지 않으려면 칸이 넉넉해야 한다. 720px 모달에서 2열이 된다 */
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2px 8px;
  border: 1px solid #e8ebed;
  border-radius: 8px;
  padding: 12px;
  max-height: 280px;
  overflow-y: auto;
}

.pick-item {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: #f6f8fa;
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: none;
  }
}

/* 이름 위, 링크 아래. 한 줄에 나란히 두면 이름부터 잘려 못 읽는다 */
.pick-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}

.pick-name {
  color: #2f3337;
  line-height: 1.35;
  /* 이름은 자르지 않는다. 긴 이름은 두 줄까지 접힌다 */
  word-break: break-word;
}

.pick-link {
  color: #acb2b9;
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-note {
  font-size: 12px;
  color: #acb2b9;
  margin: 8px 0 0;
}

/* 자동으로 붙는 탭 안내. 선택 목록 위에 둔다 */
.auto-note {
  font-size: 12px;
  color: #8d939a;
  background: #f3f5f6;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 0 0 10px;
}

.mobile-tab-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e8ebed;
}

.footer-hint {
  font-size: 12px;
  color: #acb2b9;
}

.footer-btns {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
</style>
